import {
  IconChevronLeft,
  IconChevronRight,
  IconClose,
  IconMagnifyMinusOutline,
  IconMagnifyPlusOutline,
} from 'components/icons';
import { FileWithFullUrls, MediaUnionWithFullUrls } from 'graphql/main/queries';
import { isEqual, map } from 'lodash';
import { useEffect, useState } from 'react';
import { FreeMode, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper/types';

interface Props {
  activeIndex?: number;
  images?: MediaUnionWithFullUrls[];
  onClose?: () => void;
}

const ImageViewer = ({ activeIndex = 0, images, onClose }: Props) => {
  const [privateActiveIndex, setPrivateActiveIndex] = useState(activeIndex);
  const [bigControlledSwiper, setBigControlledSwiper] = useState<SwiperType>();
  const [smallControlledSwiper, setSmallControlledSwiper] = useState<SwiperType>();

  const changeBigSlide = (swiper: SwiperType) => {
    smallControlledSwiper?.slideTo?.(swiper.realIndex, 0);
    setPrivateActiveIndex(swiper.realIndex);
  };
  const selectImage = (index: number) => {
    bigControlledSwiper?.slideTo?.(index + 1, 0);
    setPrivateActiveIndex(index);
  };

  useEffect(() => {
    setPrivateActiveIndex(activeIndex);
  }, [activeIndex]);

  return (
    <div className='fixed top-0 left-0 flex h-screen w-screen flex-col justify-between bg-[#000000]'>
      <div className='flex min-h-[60px] items-center justify-end space-x-[12px] px-[16px] text-[#ffffff]'>
        <button disabled type='button' className='opacity-75 hover:opacity-100 disabled:opacity-25'>
          <IconMagnifyMinusOutline className='min-h-[24px] min-w-[24px]' />
        </button>
        <button disabled type='button' className='opacity-75 hover:opacity-100 disabled:opacity-25'>
          <IconMagnifyPlusOutline className='min-h-[24px] min-w-[24px]' />
        </button>
        <button type='button' className='opacity-75 hover:opacity-100' onClick={onClose}>
          <IconClose className='min-h-[32px] min-w-[32px] text-text2' />
        </button>
      </div>
      <div className='relative flex h-full w-full items-center justify-center'>
        <button
          id='image-viewer-swiper-back'
          type='button'
          className='absolute left-[8px] top-1/2 z-[2] flex h-[32px] w-[32px] -translate-y-1/2 items-center justify-center rounded-[6px] bg-[#ffffff] p-[8px] opacity-90 hover:opacity-100 screen-640:left-[16px]'
        >
          <IconChevronLeft className='min-h-[24px] min-w-[24px] text-[#000000]' />
        </button>
        <div className='flex h-full w-full items-center justify-center'>
          <Swiper
            threshold={10}
            draggable
            loop
            slidesPerView={1}
            initialSlide={privateActiveIndex}
            navigation={{
              prevEl: '#image-viewer-swiper-back',
              nextEl: '#image-viewer-swiper-forward',
            }}
            modules={[Navigation]}
            className='w-full'
            onSwiper={(swiper: SwiperType) => setBigControlledSwiper(swiper)}
            onSlideChange={changeBigSlide}
          >
            {map(images, (image, imageIndex) => (
              <SwiperSlide key={`${image.fileId}-${imageIndex}`}>
                <div className='flex h-[calc(100vh-180px)] items-center justify-center'>
                  <img
                    src={(image as FileWithFullUrls)?.originalUrl ?? '/images/error-image.svg'}
                    alt='current-gallery'
                    loading='lazy'
                    className='object-cover'
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <button
          id='image-viewer-swiper-forward'
          type='button'
          className='absolute right-[8px] top-1/2 z-[2] flex h-[32px] w-[32px] -translate-y-1/2 items-center justify-center rounded-[6px] bg-[#ffffff] p-[8px] opacity-90 hover:opacity-100 screen-640:right-[16px]'
        >
          <IconChevronRight className='min-h-[24px] min-w-[24px] text-[#000000]' />
        </button>
      </div>
      <div className='min-h-[80px] p-[8px]'>
        <Swiper
          threshold={10}
          draggable
          freeMode
          slidesPerView='auto'
          initialSlide={privateActiveIndex}
          spaceBetween={8}
          modules={[FreeMode]}
          onSwiper={(swiper: SwiperType) => setSmallControlledSwiper(swiper)}
        >
          {map(images, (image, imageIndex) => (
            <SwiperSlide
              key={`${image.fileId}-${imageIndex}`}
              className='w-[auto_!important]'
              onClick={() => {
                selectImage(imageIndex);
              }}
            >
              <div
                className={`h-[64px] w-[112px] cursor-pointer overflow-hidden rounded-[6px] hover:opacity-100 ${
                  isEqual(privateActiveIndex, imageIndex)
                    ? 'border-2 border-stroke opacity-100'
                    : 'opacity-50'
                }`}
              >
                <img
                  src={(image as FileWithFullUrls)?.originalUrl ?? '/images/error-image.svg'}
                  alt={`gallery-${imageIndex}`}
                  loading='lazy'
                  className='h-full w-full object-cover'
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default ImageViewer;
